import React, { useState } from 'react';
import type { MarketProfileState } from './marketProfileStore';
import { computeTotalComp, formatRub, positionFixMonthlyValue, positionFixValue, positionHasLtip, positionLtipAmountValue, positionStiValue } from './marketProfileStore';
import { ReviewRow, SensitiveDataNotice } from './MarketProfileShared';

type Props = {
  state: MarketProfileState;
  onConfirm: () => void;
  onBack: () => void;
};

export default function ProfileReviewScreen({
  state,
  onConfirm,
  onBack,
}: Props) {
  const [agreed, setAgreed] = useState(false);
  const { data } = state.position;
  const grading = state.grading.result;

  return (
    <div className="dcf-mp-form">
      <button type="button" className="dcf-btn dcf-btn--ghost dcf-mp-back" onClick={onBack}>
        ← К профилю рынка
      </button>

      <header className="dcf-header">
        <h1 className="dcf-header__title">Проверьте данные перед отправкой</h1>
        <p className="dcf-header__desc">
          Убедитесь, что данные актуальны. От них зависит точность рыночного сравнения.
        </p>
      </header>

      <div className="dcf-mp-card dcf-mp-form-card">
        <div className="dcf-mp-review-block">
          <div className="dcf-mp-review-block__head">
            <h2 className="dcf-mp-form-section-title">Профессиональный контекст</h2>
          </div>
          <dl className="dcf-mp-review-list">
            <ReviewRow label="Индустрия" value={data.industryName} />
            <ReviewRow label="Сектор" value={data.sectorName} />
            <ReviewRow label="Компания" value={data.companyName} />
            <ReviewRow label="Должность" value={data.companyRoleName} />
          </dl>
        </div>

        <div className="dcf-mp-review-block">
          <div className="dcf-mp-review-block__head">
            <h2 className="dcf-mp-form-section-title">Компенсационный пакет</h2>
          </div>
          <dl className="dcf-mp-review-list">
            <ReviewRow
              label="Фиксированная оплата (в месяц)"
              value={formatRub(positionFixMonthlyValue(data))}
            />
            <ReviewRow label="Фиксированная оплата (в год)" value={formatRub(positionFixValue(data))} />
            <ReviewRow label="STI" value={positionStiValue(data) ? `${positionStiValue(data)}% от base` : '—'} />
            <ReviewRow label="LTIP" value={positionHasLtip(data) ? formatRub(positionLtipAmountValue(data)) : 'Нет'} />
            <ReviewRow
              label="Total Comp (target)"
              value={formatRub(computeTotalComp(data))}
            />
            <ReviewRow label="Дата актуальности" value={data.actualDate} />
          </dl>
        </div>

        <div className="dcf-mp-review-block">
          <div className="dcf-mp-review-block__head">
            <h2 className="dcf-mp-form-section-title">Грейдинг роли</h2>
          </div>
          <dl className="dcf-mp-review-list">
            <ReviewRow label="Направление" value={grading?.familyName} />
            <ReviewRow label="Зона работы" value={grading?.roleClusterName} />
            <ReviewRow
              label="Рыночный эквивалент"
              value={grading?.roleName
                ? `${grading.roleName}${grading.gradeLevel != null ? ` · G${grading.gradeLevel}` : ''}`
                : '—'}
            />
            <ReviewRow label="Сумма баллов" value={grading ? String(grading.totalScore) : '—'} />
          </dl>
        </div>

        <div className="dcf-mp-review-block">
          <div className="dcf-mp-review-block__head">
            <h2 className="dcf-mp-form-section-title">Настройки анонимности</h2>
          </div>
          <dl className="dcf-mp-review-list">
            <ReviewRow label="Отображение в сообществе" value="Через псевдоним" />
            <ReviewRow label="Компания и контакты" value="Не показываются другим участникам" />
            <ReviewRow label="Компенсационные данные" value="Только в агрегированном виде" />
          </dl>
        </div>

        <SensitiveDataNotice compact />

        <label className="dcf-toggle-row dcf-mp-confirm-row">
          <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
          Подтверждаю, что данные актуальны и могут использоваться для формирования
          агрегированных рыночных ориентиров.
        </label>

        <div className="dcf-mp-form-actions">
          <button type="button" className="dcf-btn dcf-btn--secondary" onClick={onBack}>
            Назад
          </button>
          <button
            type="button"
            className="dcf-btn dcf-btn--primary"
            disabled={!agreed}
            onClick={onConfirm}
          >
            Подтвердить и открыть данные
          </button>
        </div>
      </div>
    </div>
  );
}
